import OneChoiceQuestion from "../components/OneChoiceQuestion";
import MultipleChoiceQuestion from "../components/MultipleChoiceQuestion";
import TextQuestion from "../components/TextQuestion";
import OneChoiceWithOpenAnswer from "../components/OneChoiceWithOpenAnswer";
import NumericOneChoice from "../components/NumericOneChoice";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../LanguageContext";

const ULID_REGEX = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/i;

const PassengerSurvey = () => {
  const { t, i18n } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [surveyErrorCode, setSurveyErrorCode] = useState("");
  const [surveyLinkError, setSurveyLinkError] = useState(null); // "expired" | "invalid" | "answered"
  const [surveyType, setSurveyType] = useState("");
  const [programLevelName, setProgramLevelName] = useState("");
  const [travelClassName, setTravelClassName] = useState("");
  const [surveyParams, setSurveyParams] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setLanguage } = useLanguage();

  const loadSurveyParameters = async () => {
    const urlParam = window.location.pathname.split("/")[2];

    if (ULID_REGEX.test(urlParam)) {
      // New token-based flow
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/Survey/survey-token/${urlParam}`
      );
      const tokenData = response.data;

      const language = tokenData.language || i18n.language;
      i18n.changeLanguage(language);
      setLanguage(language);
      setSurveyType(tokenData.surveyType || "Nps");
      setProgramLevelName(tokenData.programLevelName || "");
      setTravelClassName(tokenData.travelClassName || "");

      // Only keep UI-rendering fields — PII is resolved server-side from the token
      return {
        surveyTokenId: urlParam,
        language,
        surveyType: tokenData.surveyType || "Nps",
        programLevelName: tokenData.programLevelName,
        travelClassName: tokenData.travelClassName,
        responseSource: tokenData.responseSource || null,
        channel: 0,
      };
    } else {
      // Legacy Base64 flow — unchanged
      let decoded;
      try {
        decoded = atob(urlParam);
      } catch {
        throw { response: { status: 404 } };
      }
      if (!decoded.startsWith("flynas|")) {
        throw { response: { status: 404 } };
      }
      const params = decoded.split("|");
      let language = i18n.language;
      if (params[14] && params[14].trim() !== "") {
        language = params[14].trim();
        i18n.changeLanguage(language);
        setLanguage(language);
      }
      const data = {
        email: params[1],
        workPhone: params[2],
        pnr: params[3],
        flightNumber: params[4].trim(),
        departureStation: params[5].trim(),
        arrivalStation: params[6].trim(),
        departureDate: params[7].trim(),
        language: language,
        channel: 0,
      };
      if (params.length > 8) {
        data.isInternational = params[8].trim() === "True";
        data.fromCountry = params[9].trim();
        data.toCountry = params[10].trim();
        data.flightSTDUTC = params[11].trim();
        data.flightSTAUTC = params[12].trim();
        data.fullName = params[15]?.trim();
        data.programLevelName = params[16]?.trim();
        data.travelClassName = params[17]?.trim();
        data.responseSource = params[18]?.trim() || null;
        if (!params[13]) {
          data.surveyType = "Nps";
          setSurveyType("Nps");
        } else {
          data.surveyType = params[13].trim();
          setSurveyType(data.surveyType);
        }
      }
      if (data.programLevelName) setProgramLevelName(data.programLevelName);
      if (data.travelClassName) setTravelClassName(data.travelClassName);
      return data;
    }
  };

  // Helper function to check if user is in detractor flow
  // Detractor = first question (NPS recommendation) answered with 1-6
  const isDetractorFlow = (questionsList) => {
    const firstQuestion = questionsList.find(q => q.order === 1);
    if (!firstQuestion || !firstQuestion.answer) {
      return false;
    }
    const detractorAnswers = ["1", "2", "3", "4", "5", "6"];
    return detractorAnswers.includes(firstQuestion.answer);
  };

  useEffect(() => {
    const initSurvey = async () => {
      let params;
      try {
        params = await loadSurveyParameters();
        setSurveyParams(params);
      } catch (error) {
        if (error.response?.status === 404) {
          setSurveyLinkError("invalid");
        } else if (error.response?.status === 409) {
          setSurveyLinkError("answered");
        } else if (error.response?.status === 410) {
          setSurveyLinkError("expired");
        } else {
          setSurveyErrorCode(error.response?.data?.errorCode || "");
        }
        return;
      }

      // Token flow: single opaque param — no PII in URL
      // Legacy Base64 flow: PII in query string (backward compat until old links expire)
      const queryString = params.surveyTokenId
        ? `?token=${params.surveyTokenId}`
        : `?email=${params.email}&workPhone=${params.workPhone}&homePhone=${params.homePhone}&pnr=${params.pnr}` +
          `&flightNumber=${params.flightNumber}&departureStation=${params.departureStation}&arrivalStation=${params.arrivalStation}&departureDate=${params.departureDate}` +
          `&surveyType=${params.surveyType}` +
          `&fullName=${params.fullName}`;

      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/Survey/passenger-non-answered-questions${queryString}`
        )
        .then(async (response) => {
          let allQuestions = response.data;

          // If passenger clicked a score button in the email, save Q1 using the
          // already-fetched question data, then remove it from the list in memory.
          // No second GET needed — avoids any read-after-write timing issue.
          const inlineScore = new URLSearchParams(window.location.search).get("score");
          if (inlineScore && params.surveyTokenId) {
            const parsedScore = parseInt(inlineScore, 10);
            if (!isNaN(parsedScore) && parsedScore >= 1 && parsedScore <= 10) {
              const q1 = allQuestions.find(
                (q) => q.identifier === "recommendation" || q.order === 1
              );
              if (q1) {
                try {
                  await axios.post(`${process.env.REACT_APP_BASE_URL}/Survey/passenger-questions`, {
                    ...params,
                    answers: [
                      {
                        questionId: q1.id,
                        answer: String(parsedScore),
                        openAnswer: null,
                        questionOrder: q1.order,
                      },
                    ],
                  });
                } catch {
                  // 409 = already answered — safe to ignore and continue
                }
                // Always remove Q1 from the list regardless of POST outcome
                allQuestions = allQuestions.filter((q) => q.id !== q1.id);
              }
            }
          }

          setQuestions(allQuestions);

          const isDetractor = isDetractorFlow(allQuestions);
          const isPrime = params.programLevelName === "Prime";
          const isBusiness = params.travelClassName === "Business";
          const shouldShowLastQuestion = (isPrime || isBusiness) && isDetractor;

          let lastQuestion = allQuestions.find(q => q.identifier === "CONTACT_CUSTOMER_SUPPORT");
          if (!lastQuestion) {
            lastQuestion = allQuestions.find(q =>
              q.textEnglish && q.textEnglish.toLowerCase().includes("want to be contacted by customer support")
            );
          }
          if (!lastQuestion && allQuestions.length > 0) {
            const maxOrder = Math.max(...allQuestions.map(q => q.order || 0));
            lastQuestion = allQuestions.find(q => q.order === maxOrder);
          }

          let filtered = allQuestions;
          if (!shouldShowLastQuestion && lastQuestion) {
            if (lastQuestion.identifier === "CONTACT_CUSTOMER_SUPPORT") {
              filtered = allQuestions.filter(q =>
                !q.identifier || q.identifier !== "CONTACT_CUSTOMER_SUPPORT"
              );
            } else if (lastQuestion.textEnglish && lastQuestion.textEnglish.toLowerCase().includes("want to be contacted by customer support")) {
              filtered = allQuestions.filter(q =>
                !q.textEnglish || !q.textEnglish.toLowerCase().includes("want to be contacted by customer support")
              );
            } else {
              const maxOrder = lastQuestion.order;
              filtered = allQuestions.filter(q => q.order !== maxOrder);
            }
          }

          if (filtered.length === 0) {
            setSurveyLinkError("answered");
            return;
          }

          setFilteredQuestions(filtered);
          setCurrentQuestion(filtered[0]);
        })
        .catch((error) => {
          setSurveyErrorCode(error.response.data.errorCode);
        });
    };

    initSurvey();
  }, []);

  const setQuestionAnswer = (
    answer,
    nextQuestionId,
    isAdding,
    openAnswer = ""
  ) => {
    if (currentQuestion.type === "multipleChoice" && currentQuestion.answer) {
      if (isAdding) answer = currentQuestion.answer + "," + answer;
      else {
        let answers = currentQuestion.answer.split(",");
        answers = answers.filter((a) => a !== answer);
        answer = answers.join(",");
      }
    }
    if (currentQuestion.type === "text") {
      const expr = /(<([^>]+)>)/gi;
      const found = answer.match(expr);
      if (found && found.length > 0) {
        setCurrentQuestion((oldQuestion) => ({
          ...oldQuestion,
          answer: "",
          nextQuestionId: nextQuestionId,
        }));
        return;
      }
    }
    if (currentQuestion.type === "oneChoiceWithOpenAnswer") {
      let optionHasText = currentQuestion.options.find(
        (option) => option.value === answer
      ).withText;
      if (optionHasText && !openAnswer) {
        setCurrentQuestion((oldQuestion) => ({
          ...oldQuestion,
          answer: "",
          nextQuestionId: nextQuestionId,
        }));
        return;
      }
    }
    setCurrentQuestion((oldQuestion) => ({
      ...oldQuestion,
      answer: answer,
      nextQuestionId: nextQuestionId,
      openAnswer: openAnswer,
    }));
  };

  const goToNextQuestion = () => {
    let newQuestionsList = JSON.parse(JSON.stringify(questions));
    const questionIndex = questions.findIndex(q => q.id === currentQuestion.id);
    if (questionIndex !== -1) {
      newQuestionsList[questionIndex] = JSON.parse(
        JSON.stringify(currentQuestion)
      );
    }
    setQuestions((oldQuestions) => newQuestionsList);

    // Use programLevelName and travelClassName from state — no need to re-parse URL
    const isDetractor = isDetractorFlow(newQuestionsList);
    const isPrime = programLevelName === "Prime";
    const isBusiness = travelClassName === "Business";
    const shouldShowLastQuestion = (isPrime || isBusiness) && isDetractor;

    let newFilteredQuestionsList = newQuestionsList;

    let lastQuestionInList = newQuestionsList.find(q => q.identifier === "CONTACT_CUSTOMER_SUPPORT");
    if (!lastQuestionInList) {
      lastQuestionInList = newQuestionsList.find(q =>
        q.textEnglish && q.textEnglish.toLowerCase().includes("want to be contacted by customer support")
      );
    }
    if (!lastQuestionInList && newQuestionsList.length > 0) {
      const maxOrder = Math.max(...newQuestionsList.map(q => q.order || 0));
      lastQuestionInList = newQuestionsList.find(q => q.order === maxOrder);
    }

    if (!shouldShowLastQuestion && lastQuestionInList) {
      if (lastQuestionInList.identifier === "CONTACT_CUSTOMER_SUPPORT") {
        newFilteredQuestionsList = newQuestionsList.filter(q =>
          !q.identifier || q.identifier !== "CONTACT_CUSTOMER_SUPPORT"
        );
      } else if (lastQuestionInList.textEnglish && lastQuestionInList.textEnglish.toLowerCase().includes("want to be contacted by customer support")) {
        newFilteredQuestionsList = newQuestionsList.filter(q =>
          !q.textEnglish || !q.textEnglish.toLowerCase().includes("want to be contacted by customer support")
        );
      } else {
        const maxOrder = lastQuestionInList.order;
        newFilteredQuestionsList = newQuestionsList.filter(q => q.order !== maxOrder);
      }
    }

    const filteredIndex = newFilteredQuestionsList.findIndex(q => q.id === currentQuestion.id);
    if (filteredIndex !== -1) {
      newFilteredQuestionsList[filteredIndex] = JSON.parse(
        JSON.stringify(currentQuestion)
      );
    }
    setFilteredQuestions(newFilteredQuestionsList);

    const nextQuestion = newFilteredQuestionsList.find(
      (question) => question.id === currentQuestion.nextQuestionId
    );

    if (!nextQuestion) {
      navigate(`/thank-you`);
      return;
    }

    setCurrentQuestion(nextQuestion);
    setCurrentQuestionIndex(newFilteredQuestionsList.indexOf(nextQuestion));
  };

  const saveQuestions = async (isLastQuestion) => {
    let questionAnswer = { ...surveyParams };
    questionAnswer.answers = [];
    questionAnswer.answers.push({
      questionId: currentQuestion.id,
      answer: currentQuestion.answer,
      openAnswer: currentQuestion.openAnswer,
      questionOrder: currentQuestion.order,
    });

    setIsSubmitting(true);
    return axios
      .post(
        `${process.env.REACT_APP_BASE_URL}/Survey/passenger-questions`,
        questionAnswer
      )
      .then((response) => {
        if (isLastQuestion) navigate(`/thank-you`);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div>
      <section className="section section-lg welcome">
        <div className="container">
          <h1>{t("thanks_for_choose_flynas")}</h1>
          <h2>{t("help_to_improve_experience")}</h2>
        </div>
      </section>
      {currentQuestion ? (
        <>
          {currentQuestion.type === "numericOneChoice" ? (
            <NumericOneChoice
              question={currentQuestion}
              setQuestionAnswer={setQuestionAnswer}
            />
          ) : currentQuestion.type === "multipleChoice" ? (
            <MultipleChoiceQuestion
              question={currentQuestion}
              setQuestionAnswer={setQuestionAnswer}
            />
          ) : currentQuestion.type === "text" ? (
            <TextQuestion
              question={currentQuestion}
              setQuestionAnswer={setQuestionAnswer}
              surveyType={surveyType}
            />
          ) : currentQuestion.type === "oneChoiceWithOpenAnswer" ? (
            <OneChoiceWithOpenAnswer
              question={currentQuestion}
              setQuestionAnswer={setQuestionAnswer}
            />
          ) : currentQuestion.type === "oneChoice" ? (
            <OneChoiceQuestion
              question={currentQuestion}
              setQuestionAnswer={setQuestionAnswer}
            />
          ) : (
            <div>No template for this question type</div>
          )}
          <button
            className="btnSubmit"
            disabled={
              isSubmitting ||
              (!currentQuestion.answer && currentQuestion.type !== "text")
            }
            onClick={async () => {
              const isLastQuestion = currentQuestionIndex === filteredQuestions.length - 1;
              const isEmptyTextAnswer = currentQuestion.type === "text" && !currentQuestion.answer;
              if (!isEmptyTextAnswer) {
                await saveQuestions(isLastQuestion);
              }
              if (!isLastQuestion) {
                goToNextQuestion();
              } else if (isEmptyTextAnswer) {
                navigate(`/thank-you`);
              }
            }}
          >
            {currentQuestion.type === "text" ? (
              currentQuestion.answer ? <span>{t("send")}</span> : <span>{t("skip")}</span>
            ) : currentQuestionIndex === filteredQuestions.length - 1 ? (
              <span>{t("submit")}</span>
            ) : (
              <span>{t("next")}</span>
            )}
          </button>
        </>
      ) : surveyLinkError === "answered" ? (
        <h3>{t("survey_answered_previously")}</h3>
      ) : surveyLinkError === "expired" ? (
        <h3>{t("survey_link_expired")}</h3>
      ) : surveyLinkError === "invalid" ? (
        <h3>{t("survey_link_invalid")}</h3>
      ) : surveyErrorCode && surveyErrorCode === "11" ? (
        <h3>{t("survey_answered_previously")}</h3>
      ) : (
        <h3>{t("load_survey")} .....</h3>
      )}
      <br />
      <br />
    </div>
  );
};

export default PassengerSurvey;
